import { type MemberExtraFragment } from '@/affiliate-dashboard/graphql';
import { Avatar, AvatarFallback } from '@/components/base/avatar';
import { Label } from '@/components/base/label';
import { cn } from '@/utils';
import { countries } from '@/utils/country-list';
import { getConsistentColor } from '@/utils/randomizer';
import { faMoneyBill, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactCountryFlag from 'react-country-flag';
import { useIntl } from 'react-intl';

export function Member({
  subAffiliate,
  onClick,
  level,
  className,
}: {
  subAffiliate: MemberExtraFragment;
  onClick?: (member: MemberExtraFragment) => void;
  level: number;
  className?: string;
}): JSX.Element {
  const { email, countryName, totalReferrals, monthlyRevenue, id } =
    subAffiliate;
  const intl = useIntl();
  const code =
    countries.find((country) => country.name === countryName)?.value ?? '';
  const color = getConsistentColor(id ?? '');

  return (
    <button
      onClick={() => {
        Boolean(totalReferrals) && onClick && onClick(subAffiliate);
      }}
      className={cn(
        'cursor-pointer gap-1 border-b border-l border-default-gray-900 px-2 py-2 transition-all duration-200 last:border-b-0 hover:bg-default-gray-100 dark:hover:bg-default-gray-900 sm:py-3.5 md:inline-flex md:items-center md:gap-4',
        {
          'cursor-default hover:bg-transparent dark:hover:bg-transparent':
            !totalReferrals,
        },
        className,
      )}
      type="button"
      style={{ marginLeft: `${level * 20}px` }}
    >
      <div
        className={cn(
          'w-full translate-x-[-30px] cursor-pointer items-center justify-between gap-3 rounded',
          { 'cursor-default': !totalReferrals },
        )}
      >
        <div className="flex items-center gap-2">
          <Avatar className="relative overflow-visible border-none">
            <AvatarFallback
              className="bg-opacity-25"
              style={{
                backgroundColor: color,
                color: 'white',
              }}
            >
              {subAffiliate.initials !== 'null' ? subAffiliate.initials : 'NN'}
            </AvatarFallback>
            {countryName && (
              <div className="absolute -bottom-1 right-0 z-40">
                <ReactCountryFlag
                  countryCode={code}
                  style={{
                    height: '1.1rem',
                    width: '1.1rem',
                    borderRadius: '20%',
                    objectFit: 'cover',
                  }}
                  svg
                />
              </div>
            )}
          </Avatar>

          <p className="overflow-hidden text-ellipsis text-sm font-medium">
            {email}
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-end gap-2 text-sm">
        <div className="flex justify-start gap-1">
          {Boolean(monthlyRevenue) && (
            <Label
              type="outlined"
              variant="green"
              className="my-0 flex items-center gap-1 rounded py-px text-[10px] text-xs"
            >
              {intl.formatNumber(monthlyRevenue ?? 0, {
                style: 'currency',
                currency: 'USD',
              })}
              <FontAwesomeIcon icon={faMoneyBill} />
            </Label>
          )}
          {Boolean(totalReferrals) && (
            <Label
              type="outlined"
              variant="gray"
              className="my-0 flex items-center gap-1 rounded py-px text-[10px] text-xs"
            >
              {totalReferrals}
              <FontAwesomeIcon icon={faUsers} />
            </Label>
          )}
        </div>
      </div>
    </button>
  );
}
